/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useHover } from "react-aria";

import { cssFns } from "@superweb/css";
import { useLocale } from "@superweb/intl";

import { useShimmer } from "../shimmer/shimmer";
import { useUiColors } from "../theme";
import { Placeholder } from "./placeholder";
import type { PlaceholderAction, PlaceholderLink } from "./placeholder";
import type {
  ActionsColumnSchema,
  Column,
  ColumnsSchema,
  LinkProps,
  RowActions,
  RowPositions,
  SortingDirection,
} from "./types";
import { Cell } from "./cells/cells";
import { HeaderCell } from "./cells/header-cells";
import { CheckboxCell, CheckboxHeaderCell } from "./cells/checkbox-cells";
import { RowCell } from "./cells/row-cell";
import { useFocusState, type FocusState } from "./use-focus-state";
import { getAlign, getColSpan } from "./cells/cell-parameters";
import { ActionsCell, ActionsHeaderCell } from "./cells/actions-cells";

const SELECTION_CELL_WIDTH = 56;

export type SelectionState =
  | {
      /**
       * All items are selected, including not yet fetched,
       * except items in `exclude` set.
       */
      all: true;
      /**
       * All items except these are selected.
       */
      exclude: Set<string>;
    }
  | {
      /**
       * Only items in `include` set are selected.
       */
      all: false;
      /**
       * Only these items are selected.
       */
      include: Set<string>;
    };

export const Table = <D extends {}>({
  data,
  columns,
  progress,
  sort,
  link,
  onPress,
  onSortChange,
  placeholder,
  stickyShift,
  selection,
  variant = "fill",
  stickyRow,
  stickyFirstColumn = false,
  stickyFirstColumnShift = 0,
  actions,
}: (
  | {
      stickyRow?: (data: D) => boolean;
      stickyFirstColumn?: never;
      stickyFirstColumnShift?: never;
    }
  | {
      stickyRow?: never;
      stickyFirstColumn?: boolean;
      stickyFirstColumnShift?: number;
    }
) & {
  data: D[];
  columns: (Column<D> | undefined)[];
  progress?: boolean;
  sort?: [string, SortingDirection];
  link?: (data: D) => LinkProps | undefined;
  onPress?: (data: D) => void;
  onSortChange?: (sort?: [string, SortingDirection]) => void;

  /**
   * The list of actions that are located at the end of the table row in the last column.
   *
   * @param rowData - Element from array `data` of relevant current row.
   * @returns array of actions to display
   */
  actions?: RowActions<D>;

  placeholder?: {
    title: string;
    description?: string;
    actions?: PlaceholderAction[];
    links?: PlaceholderLink[];
    image?: ReactNode;
  };

  stickyShift?: string;
  /**
   * Adds selection in the table.
   *
   * To determine that at least one row is selected in the table, you can use this condition:
   * ```
   * state.all || state.include.size
   * ```
   */
  selection?: {
    state: SelectionState;
    onChange: (state: SelectionState) => void;

    /**
     * Returns stable unique string for each item in data that will be used to identify selected rows.
     * @example
     * ```
     * key: (row) => row.id
     * ```
     */
    key: (row: D) => string;

    /**
     * Function that determines if a specific row should be disabled for selection.
     * Return true to make the row non-selectable and non-zero string to show disabled reason in the tooltip.
     *
     * @example
     * ```
     * // With tooltip
     * disabled: (row) => row.selectedBefore ? "Used in the previous step" : false
     * ```
     *
     * @example
     * ```
     * // Without tooltip
     * disabled: (row) => row.selectedBefore
     * ```
     */
    disabled?: (row: D) => boolean | string;
  };
  /**
   * Variant of table appearance.
   *
   * 'ghost' is for location NOT on uiColors.background (better use with uiColors.backgroundMinor),
   * because it has specific header and transparent gaps between rows.
   *
   * 'fill' is already quite outdated.
   * @defaultValue 'fill'
   */
  variant?: "fill" | "ghost";
}) => {
  const tableRef = useRef<HTMLTableElement>(null);
  const tableHeaderRef = useRef<HTMLTableSectionElement>(null);
  const tableFirstRow = useRef<HTMLTableRowElement>(null);
  const pinnedTableRow = usePinnedTableRow();

  const [tableHeaderHeight, setTableHeaderHeight] = useState(0);
  const [tableFirstCellWidth, setTableFirstCellWidth] = useState(0);
  const [isStickyFirstColumn, setIsStickyFirstColumn] = useState(false);
  const locale = useLocale();
  const isRTL = locale.textInfo.direction === "rtl";

  const calculateTableSchema = <D extends {}>(
    columns: (Column<D> | undefined)[],
  ): [ColumnsSchema<D>, ActionsColumnSchema<D> | undefined] => {
    const columnsSchema: ColumnsSchema<D> = [];

    const relevantColumns = columns.filter((column): column is Column<D> =>
      Boolean(column),
    );

    let colIndex = 0;
    relevantColumns.forEach((column) => {
      columnsSchema.push({
        column,
        colIndex,
      });

      const colSpan = getColSpan(column);
      colIndex += colSpan;
    });

    const actionsColumnSchema = actions
      ? {
          actions: actions as RowActions<{}>,
          colIndex,
        }
      : undefined;

    return [columnsSchema, actionsColumnSchema];
  };

  const calculateFocusLimits = <D extends {}>(
    columnsSchema: ColumnsSchema<D>,
    data: D[],
    selection: boolean,
  ) => {
    const isHeaderFocusable =
      columnsSchema
        .map((schema) => schema.column)
        .some(
          (column) =>
            column.sort ||
            (column.type === undefined && column.sortable) ||
            (column.type !== undefined && column.headerTooltip),
        ) || selection;

    return {
      top: isHeaderFocusable ? -1 : 0,
      bottom: data.length - 1,
    };
  };

  const [columnsSchema, actionsColumnSchema] = calculateTableSchema(columns);
  const focusLimits = calculateFocusLimits(columnsSchema, data, !!selection);

  const focusState = useFocusState({
    ref: tableRef,
    limits: focusLimits,
  });

  const headerCheckboxRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setTableHeaderHeight(tableHeaderRef.current?.clientHeight || 0);
  }, [tableRef, setTableHeaderHeight]);

  const calcTableFirstCellWidth = useCallback(() => {
    if (selection) {
      setTableFirstCellWidth(SELECTION_CELL_WIDTH);
      return;
    }

    if (tableFirstRow.current && tableFirstRow.current.firstChild) {
      const width = (tableFirstRow.current.firstChild as HTMLTableCellElement)
        .offsetWidth;

      setTableFirstCellWidth(width);
    }
  }, [tableFirstRow, setTableFirstCellWidth, selection]);

  useEffect(() => {
    const callback = () => {
      if (!tableRef.current || stickyFirstColumn !== true) {
        return;
      }

      setIsStickyFirstColumn(
        isRTL
          ? window.innerWidth -
              tableRef.current.offsetLeft -
              tableRef.current.offsetWidth +
              window.scrollX <
              (stickyFirstColumnShift || 0)
          : tableRef.current.offsetLeft - window.scrollX <
              (stickyFirstColumnShift || 0),
      );
    };

    callback();

    window.addEventListener("scroll", callback);
    window.addEventListener("resize", callback);

    return () => {
      window.removeEventListener("resize", callback);
      window.removeEventListener("scroll", callback);
    };
  }, [isRTL, stickyFirstColumn, stickyFirstColumnShift]);

  useEffect(() => {
    window.addEventListener("scroll", calcTableFirstCellWidth);
    window.addEventListener("resize", calcTableFirstCellWidth);

    return () => {
      window.removeEventListener("resize", calcTableFirstCellWidth);
      window.removeEventListener("scroll", calcTableFirstCellWidth);
    };
  }, [calcTableFirstCellWidth]);

  useEffect(() => {
    calcTableFirstCellWidth();
  }, [calcTableFirstCellWidth, isRTL]);

  const canRenderSelection =
    selection && tableFirstCellWidth <= SELECTION_CELL_WIDTH;

  const hasFooter = columnsSchema
    .map((schema) => schema.column)
    .some((column) => "footer" in column && column.footer);

  return (
    <div css={{ display: "grid" }}>
      {data.length === 0 ? (
        <Placeholder
          title={placeholder?.title}
          description={placeholder?.description}
          actions={placeholder?.actions}
          links={placeholder?.links}
          progress={progress}
          image={placeholder?.image}
        />
      ) : (
        <>
          <table
            ref={tableRef}
            role="grid"
            css={{
              alignSelf: "start",
              isolation: "isolate",
              gridColumnStart: "1",
              gridColumnEnd: "1",
              gridRowStart: "1",
              gridRowEnd: "1",

              borderSpacing: "0",
              // grid doesn't stretch tables in Safari
              width: "100%",
            }}
          >
            <thead ref={tableHeaderRef}>
              <tr ref={tableFirstRow}>
                {selection && canRenderSelection && (
                  <CheckboxHeaderCell
                    focusState={focusState}
                    stickyShift={stickyShift}
                    selection={selection.state}
                    onChange={selection.onChange}
                    inputRef={headerCheckboxRef}
                    variant={variant}
                    isStickyFirstColumn={
                      stickyFirstColumn && isStickyFirstColumn
                    }
                    stickyFirstColumnShift={stickyFirstColumnShift}
                    progress={progress}
                  />
                )}
                {columnsSchema.map(({ column, colIndex }) => {
                  const shiftColIndex = selection ? colIndex + 1 : colIndex;

                  return (
                    <HeaderCell
                      key={column.id}
                      column={column}
                      index={shiftColIndex}
                      focusState={focusState}
                      variant={variant}
                      sortState={sort}
                      onSortChange={onSortChange}
                      isStickyFirstColumn={
                        stickyFirstColumn && isStickyFirstColumn
                      }
                      stickyFirstColumnShift={stickyFirstColumnShift}
                      stickyShift={stickyShift}
                      hasSelection={Boolean(selection)}
                      selectionOffset={selection ? tableFirstCellWidth : 0}
                    />
                  );
                })}
                {actionsColumnSchema ? (
                  <ActionsHeaderCell
                    index={actionsColumnSchema.colIndex}
                    focusState={focusState}
                    variant={variant}
                    stickyShift={stickyShift}
                  />
                ) : undefined}
              </tr>
            </thead>
            <tbody>
              {data.map((row, index) => (
                <Row
                  key={index}
                  row={row}
                  rowIndex={index}
                  columnsSchema={columnsSchema}
                  actionsColumnSchema={actionsColumnSchema}
                  focusState={focusState}
                  stickyRow={stickyRow}
                  hasSelection={Boolean(selection)}
                  selectionOffset={selection ? tableFirstCellWidth : 0}
                  isStickyFirstColumn={stickyFirstColumn && isStickyFirstColumn}
                  stickyFirstColumnShift={stickyFirstColumnShift}
                  tableHeaderHeight={tableHeaderHeight}
                  link={link}
                  onPress={onPress}
                  selection={canRenderSelection ? selection : undefined}
                  variant={variant}
                  positions={{
                    top: index === 0,
                    bottom: !hasFooter && index === data.length - 1,
                  }}
                  progress={progress}
                />
              ))}
              {hasFooter && (
                <tr aria-rowindex={data.length + 1}>
                  {columnsSchema.map(({ column, colIndex }) => {
                    const shiftColIndex = selection ? colIndex + 1 : colIndex;
                    const align = getAlign(column);

                    return (
                      <Cell
                        isFooter
                        isPinnedRow={
                          pinnedTableRow && pinnedTableRow.pinnedRow(data)
                        }
                        key={column.id}
                        rowIndex={data.length}
                        colIndex={shiftColIndex}
                        focusState={focusState}
                        align={align}
                        variant={variant}
                        positions={{
                          bottom: true,
                          start: colIndex === 0,
                          end: colIndex === columnsSchema.length - 1,
                        }}
                        hasSelection={Boolean(selection)}
                        selectionOffset={selection ? tableFirstCellWidth : 0}
                      >
                        {"footer" in column && column.footer}
                      </Cell>
                    );
                  })}
                </tr>
              )}
            </tbody>
          </table>
          {progress && <Progress variant={variant} />}
        </>
      )}
    </div>
  );
};

const Progress = ({ variant }: { variant?: "fill" | "ghost" }) => {
  const shimmer = useShimmer();

  return (
    <div
      css={{
        ...cssFns.overflow("hidden"),
        pointerEvents: "none",
        gridColumnStart: "1",
        gridColumnEnd: "1",
        gridRowStart: "1",
        gridRowEnd: "1",
        display: "grid",
        ...(variant === "ghost" && {
          ...cssFns.border({ radius: "26px" }),
          marginBlockStart: "60px",
        }),
      }}
    >
      <div css={shimmer} />
    </div>
  );
};

const Row = <D extends { id?: string }>({
  row,
  rowIndex,
  columnsSchema,
  actionsColumnSchema,
  focusState,
  link,
  onPress,
  selection,
  variant = "fill",
  positions,
  stickyRow,
  isStickyFirstColumn,
  stickyFirstColumnShift,
  tableHeaderHeight,
  hasSelection,
  selectionOffset,
  progress,
}: {
  row: D;
  rowIndex: number;
  columnsSchema: ColumnsSchema<D>;
  actionsColumnSchema?: ActionsColumnSchema<D>;
  focusState: FocusState;
  link?: (data: D) => LinkProps | undefined;
  onPress?: (data: D) => void;
  selection?: {
    state: SelectionState;
    onChange: (state: SelectionState) => void;
    key: (row: D) => string;
    disabled?: (row: D) => boolean | string;
  };
  variant?: "fill" | "ghost";
  positions?: RowPositions;
  stickyRow?: (data: D) => boolean;
  isStickyFirstColumn?: boolean;
  stickyFirstColumnShift?: number;
  tableHeaderHeight?: number;
  hasSelection: boolean;
  selectionOffset: number;
  progress?: boolean;
}) => {
  const uiColors = useUiColors();
  const pinnedTableRow = usePinnedTableRow();
  const [height, setHeight] = useState(window.innerHeight);
  const isPinnedRow = !!pinnedTableRow?.pinnedRow(row);

  // In the case of pinned a row in the table, the row should also have sticky behavior.
  const isStickyRow = pinnedTableRow ? isPinnedRow : stickyRow?.(row);

  const [isStickyRowTop, setStickyRowTop] = useState(false);
  const [isStickyRowBottom, setStickyRowBottom] = useState(false);

  const { hoverProps, isHovered } = useHover({});

  const ref = useRef<HTMLTableRowElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const updateDimensions = () => {
      setHeight(window.innerHeight);
    };
    window.addEventListener("resize", updateDimensions);
    return () => window.removeEventListener("resize", updateDimensions);
  }, []);

  useEffect(() => {
    if (!ref.current || !isStickyRow) {
      setStickyRowTop(false);
      return;
    }

    const offset = 1 + (tableHeaderHeight || 0);

    const observer = new IntersectionObserver(
      ([event]) => {
        const pined = event ? event.intersectionRatio < 1 : false;
        let overscreen = false;

        if (ref.current) {
          const viewportOffset = ref.current.getBoundingClientRect();
          overscreen = viewportOffset.top < 16 + viewportOffset.height;
        }

        setStickyRowTop(pined && overscreen);
      },
      {
        root: document,
        threshold: 1,
        rootMargin: `${-offset}px 1000px 0px 1000px`,
      },
    );
    observer.observe(ref.current);

    return () => observer.disconnect();
  }, [isStickyRow, tableHeaderHeight, height]);

  useEffect(() => {
    if (!ref.current || !isStickyRow) {
      setStickyRowBottom(false);
      return;
    }

    const observer = new IntersectionObserver(
      ([event]) => {
        const pined = event ? event.intersectionRatio < 1 : false;

        let overscreen = false;
        if (ref.current) {
          const viewportOffset = ref.current.getBoundingClientRect();
          overscreen =
            viewportOffset.top + viewportOffset.height + 16 >
            window.innerHeight;
        }

        setStickyRowBottom(overscreen && pined);
      },
      {
        root: document,
        threshold: 1,
        rootMargin: `0px 10000px ${-1}px 10000px`,
      },
    );
    observer.observe(ref.current);

    return () => observer.disconnect();
  }, [isStickyRow, tableHeaderHeight, height]);

  return (
    <tr
      ref={ref}
      key={rowIndex}
      aria-rowindex={rowIndex + 1}
      {...hoverProps}
      css={{
        ...(isStickyRow && {
          position: "sticky",
          // Workaround for safari and cell border-spacing
          top: tableHeaderHeight ? `${tableHeaderHeight - 4}px` : "0",
          bottom: "0px",
          zIndex: "2",
        }),
        ...((isStickyRowTop ||
          isStickyRowBottom ||
          pinnedTableRow?.pinnedRow(row)) && {
          backgroundColor: uiColors.background,
        }),
      }}
    >
      {selection && (
        <CheckboxCell
          rowIndex={rowIndex}
          focusState={focusState}
          checkboxKey={selection.key(row)}
          disabled={selection.disabled?.(row)}
          selection={selection.state}
          onChange={selection.onChange}
          inputRef={inputRef}
          isHovered={isHovered}
          isStickyFirstColumn={isStickyFirstColumn}
          stickyFirstColumnShift={stickyFirstColumnShift}
          stickyRowPositions={{
            top: isStickyRowTop,
            bottom: isStickyRowBottom,
          }}
          variant={variant}
          positions={{ ...positions, start: true }}
          progress={progress}
        />
      )}
      {columnsSchema.map(({ column, colIndex }, index) => {
        const shiftColIndex = selection ? colIndex + 1 : colIndex;

        return (
          <RowCell
            rowIndex={rowIndex}
            colIndex={shiftColIndex}
            row={row}
            rowLink={link}
            onRowPress={onPress}
            focusState={focusState}
            isHovered={isHovered}
            key={column.id}
            column={column}
            variant={variant}
            hasSelection={hasSelection}
            selectionOffset={selectionOffset}
            isStickyFirstColumn={isStickyFirstColumn}
            stickyFirstColumnShift={stickyFirstColumnShift}
            stickyRowPositions={{
              top: isStickyRowTop,
              bottom: isStickyRowBottom,
            }}
            positions={{
              ...positions,
              start: shiftColIndex === 0,
              end: !actionsColumnSchema && index === columnsSchema.length - 1,
            }}
          />
        );
      })}
      {actionsColumnSchema ? (
        <ActionsCell
          rowIndex={rowIndex}
          colIndex={actionsColumnSchema.colIndex}
          actions={actionsColumnSchema.actions}
          row={row}
          focusState={focusState}
          variant={variant}
          isHovered={isHovered}
          hasSelection={hasSelection}
          selectionOffset={selectionOffset}
          isStickyFirstColumn={isStickyFirstColumn}
          stickyFirstColumnShift={stickyFirstColumnShift}
          stickyRowPositions={{
            top: isStickyRowTop,
            bottom: isStickyRowBottom,
          }}
          positions={{
            ...positions,
            start: actionsColumnSchema.colIndex === 0,
            end: true,
          }}
          onPress={onPress ? () => onPress(row) : undefined}
        />
      ) : undefined}
    </tr>
  );
};

export const useTableSelectionState = (defaultValue?: SelectionState) => {
  return useState<SelectionState>(
    defaultValue ?? {
      all: false,
      include: new Set<string>(),
    },
  );
};

export const createTableSelectionState = (
  defaultValue?: SelectionState,
): SelectionState => {
  return (
    defaultValue ?? {
      all: false,
      include: new Set<string>(),
    }
  );
};
type PinnedRowContextValue = {
  pinnedRow: (data: unknown) => boolean;
};

const PinnedRowContext = createContext<PinnedRowContextValue | undefined>(
  undefined,
);

type PinnedRowProps<D> = {
  children: ReactNode;
  pinnedRow?: (data: D) => boolean;
};

export const PinnedTableRow = <D,>({
  children,
  pinnedRow,
}: PinnedRowProps<D>) => {
  if (!pinnedRow) {
    return <>{children}</>;
  }

  return (
    <PinnedRowContext.Provider
      value={{
        pinnedRow: (data: unknown) => pinnedRow(data as D),
      }}
    >
      {children}
    </PinnedRowContext.Provider>
  );
};

export const usePinnedTableRow = () => {
  return useContext(PinnedRowContext);
};
